import { createLink } from '@tanstack/react-router'
import { xwColors } from '@xweather/theme'
import { type ComponentProps, type ReactNode, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { logout } from '../api/helpers.ts'
import { Box } from '../components/Box.tsx'
import { Avatar, Button, Divider, ListItemIcon, Menu, MenuItem, NavMenuItem, Paper, Tooltip } from '../components/generic.tsx'
import { I18n } from '../components/I18n.tsx'
import { Icon } from '../components/Icon.tsx'
import { IconButton } from '../components/IconButton.tsx'
import { Restricted } from '../components/Restricted.tsx'
import { Stack } from '../components/Stack.tsx'
import { useMenu } from '../components/useMenu.ts'
import { XweatherLogo } from '../components/XweatherLogo.tsx'
import { useDevices } from '../data/devices.ts'
import { useImpersonation, useImpersonationActions } from '../data/impersonation.ts'
import { useSubscription } from '../data/service.ts'
import { currentUserData } from '../data/userProfile.ts'
import { useReportsRoles } from '../data/users.ts'
import { tourId } from '../Intercom.tsx'
import { ids } from '../ids.ts'
import { useFlags } from '../launchdarkly.ts'
import { useDrawer } from '../states.ts'
import { Clock } from './Clock.tsx'
import { Preferences } from './preferences/Preferences.tsx'
import { ImpersonationSelect } from './serviceManagement/accounts/ImpersonationSelect.tsx'

export const Navigation = () => {
	const reportsRoles = useReportsRoles()
	return (
		<NavigationBase
			extraItems={
				<>
					<Restricted role={['ADMIN', 'LIMITED_ADMIN']}>
						<AddMenu />
					</Restricted>

					<Restricted role={['SUPER_ADMIN', 'INTERNAL_READ_ONLY']}>
						<ImpersonationBanner />
					</Restricted>
				</>
			}
		>
			<Restricted feature='protections'>
				<NavButton {...tourId('nav-dashboard')} data-ta={ids.navigationBar.dashboard} to='/impacts'>
					<I18n i18nKey={($) => $.navigation.dashboard} />
				</NavButton>
			</Restricted>
			<NavButton data-ta={ids.navigationBar.map} to='/' search hash activeOptions={{ includeSearch: false }}>
				<I18n i18nKey={($) => $.navigation.map} />
			</NavButton>
			<Restricted feature='devices'>
				<NavButton data-ta={ids.navigationBar.measurements} to='/measurements'>
					<I18n i18nKey={($) => $.navigation.measurements} />
				</NavButton>
			</Restricted>
			<Restricted feature='analyze' role={reportsRoles}>
				<NavButton data-ta={ids.navigationBar.reports} to='/reports'>
					<I18n i18nKey={($) => $.navigation.reports} />
				</NavButton>
			</Restricted>
		</NavigationBase>
	)
}

const NavigationBase = ({ children, extraItems }: { children: ReactNode; extraItems?: ReactNode }) => {
	const { setSelectedDrawer } = useDrawer()
	return (
		<>
			<Paper
				component={Stack}
				center
				elevation={5}
				sx={{ h: 'min-content', bgcolor: (t) => t.palette.brand.main, color: (t) => t.palette.brand.contrastText, px: 1 }}
			>
				<XweatherLogo data-ta={ids.navigationBar.xweatherLogo} sx={{ h: 48, p: 0.5 }} />
				<NavDivider />
				{children}
				<NavDivider />
				<HamburgerMenu />
				<ProfileMenu onPreferenceClick={() => setSelectedDrawer('preferences')} />
				<NavDivider />
				<Clock />
			</Paper>

			{extraItems}
			<Preferences />
		</>
	)
}

const NavDivider = () => <Divider orientation='vertical' flexItem sx={{ borderColor: (t) => t.palette.grey[700] }} />

export const NavButton = createLink((props: ComponentProps<typeof Button>) => (
	<Button
		{...props}
		variant='text'
		sx={{
			fontSize: '1rem',
			backgroundColor: 'transparent',
			color: (t) => t.palette.brand.contrastText,
			height: 40,
			px: 1.5,
			':hover': { bgcolor: 'rgba(255,255,255,0.1)' },
			'&.active': { bgcolor: 'rgba(255,255,255,0.2)' },
			...props.sx,
		}}
	/>
))

const API_DOCS_URL =
	'https://docs.vaisala.com/search/all?value-filters=Document_type~%2522Technical+description%2522*Product~%2522Xweather+Protect%252C+Xweather+Optimize%252C+Xweather+Observe%2522*ft%253AisPublication~%2522true%2522&content-lang=en-US'

const HamburgerMenu = () => {
	const { t } = useTranslation()
	const { data: sub } = useSubscription()
	const { openMenu, menuProps } = useMenu()

	const helpCenterLink = useMemo(() => {
		const { features } = sub
		if (features.includes('protections')) return 'https://support.xweather.com/protect'
		if (features.includes('devices') && features.includes('xcast')) return 'https://support.xweather.com/optimize'
		if (features.includes('devices')) return 'https://support.xweather.com/observe'

		// Default to Protect help center if nothing above matches
		return 'https://support.xweather.com/protect'
	}, [sub])

	return (
		<>
			<IconButton
				data-ta={ids.navigationBar.hamburgerMenuBtn}
				{...tourId('hamburger-menu')}
				variant='text'
				sx={{ color: (t) => t.palette.brand.contrastText, ':hover': { bgcolor: 'rgba(255,255,255,0.1)' } }}
				icon='Bars'
				label={t(($) => $.navigation.menuTitle)}
				onClick={openMenu}
			/>
			<Menu {...menuProps} sx={{ mt: 0.5 }}>
				<Restricted role={['ADMIN', 'LIMITED_ADMIN']}>
					<NavMenuItem data-ta={ids.navigationBar.hamburgerMenuUsers} color='primary' to='/users'>
						<ListItemIcon>
							<Icon name='UserGroup' />
						</ListItemIcon>
						{t(($) => $.navigation.users)}
					</NavMenuItem>
				</Restricted>
				<Restricted feature='devices'>
					<Restricted role='ADMIN'>
						<NavMenuItem data-ta={ids.navigationBar.hamburgerMenuApiKeys} color='primary' to='/api-keys'>
							<ListItemIcon>
								<Icon name='KeySkeleton' />
							</ListItemIcon>
							{t(($) => $.navigation.apiKeys)}
						</NavMenuItem>
					</Restricted>
					<MenuItem
						data-ta={ids.navigationBar.hamburgerMenuApiDocs}
						color='primary'
						component='a'
						href={API_DOCS_URL}
						target='_blank'
						rel='noopener noreferrer'
					>
						<ListItemIcon>
							<Icon name='Code' />
						</ListItemIcon>
						{t(($) => $.navigation.apiDocumentation)}
					</MenuItem>
					<NavMenuItem data-ta={ids.navigationBar.hamburgerMenuDevices} color='primary' to='/devices'>
						<ListItemIcon>
							<Icon name='SignalStream' />
						</ListItemIcon>
						{t(($) => $.navigation.devices)}
					</NavMenuItem>
				</Restricted>
				<MenuItem
					component='a'
					data-ta={ids.navigationBar.hamburgerMenuHelpCenter}
					color='primary'
					href={helpCenterLink}
					target='_blank'
					rel='noopener noreferrer'
				>
					<ListItemIcon>
						<Icon name='CircleQuestion' />
					</ListItemIcon>
					{t(($) => $.navigation.helpCenter)}
				</MenuItem>
				<MenuItem component='a' data-ta={ids.navigationBar.hamburgerMenuSupport} color='primary' href='mailto:support@xweather.com'>
					<ListItemIcon>
						<Icon name='CircleQuestion' />
					</ListItemIcon>
					{t(($) => $.navigation.support)}
				</MenuItem>
				<Restricted role='ADMIN'>
					<Divider />
					<NavMenuItem data-ta={ids.navigationBar.hamburgerMenuSubscription} color='primary' to='/subscription'>
						<ListItemIcon>
							<Icon name='MoneyCheckPen' />
						</ListItemIcon>
						{t(($) => $.navigation.subscription)}
					</NavMenuItem>
				</Restricted>
				<Restricted role={['SUPER_ADMIN', 'INTERNAL_READ_ONLY']}>
					<Divider />
					<NavMenuItem data-ta={ids.navigationBar.hamburgerMenuServiceMap} color='primary' to='/smp/serviceMap'>
						<ListItemIcon>
							<Icon name='Map' />
						</ListItemIcon>
						{t(($) => $.navigation.serviceMap)}
					</NavMenuItem>
					<NavMenuItem data-ta={ids.navigationBar.hamburgerMenuAccounts} color='primary' to='/smp/accounts'>
						<ListItemIcon>
							<Icon name='Building' />
						</ListItemIcon>
						{t(($) => $.navigation.accounts)}
					</NavMenuItem>
					<Restricted role={['SUPER_ADMIN']}>
						<NavMenuItem data-ta={ids.navigationBar.hamburgerMenuWebhooks} color='primary' to='/smp/webhooks/{-$webhookId}'>
							<ListItemIcon>
								<Icon name='Webhook' />
							</ListItemIcon>
							{t(($) => $.navigation.webhooks)}
						</NavMenuItem>
					</Restricted>
					<NavMenuItem data-ta={ids.navigationBar.hamburgerMenuWebRelays} color='primary' to='/smp/webRelays'>
						<ListItemIcon>
							<Icon name='LightSwitch' />
						</ListItemIcon>
						{t(($) => $.navigation.webRelays)}
					</NavMenuItem>
				</Restricted>
			</Menu>
		</>
	)
}

export const ProfileMenu = ({ onPreferenceClick }: { onPreferenceClick: () => void }) => {
	const { t } = useTranslation()
	const { openMenu, closeMenu, menuProps } = useMenu()
	const { data: currentUser } = currentUserData.use()
	const names = currentUser.fullName.split(' ')
	const initials = (names.at(0)?.at(0) ?? '') + (names.length > 1 ? (names.at(-1)?.at(0) ?? '') : '')
	return (
		<>
			<Box flex center height='100%'>
				<Avatar
					data-ta={ids.navigationBar.avatarBtn}
					{...tourId('profile-menu')}
					component={Button}
					sx={{ mr: 1, ':hover': { bgcolor: xwColors.green[300] } }}
					onClick={openMenu}
				>
					{initials || <Icon name='User' />}
				</Avatar>
			</Box>
			<Menu {...menuProps} sx={{ mt: 1 }}>
				<MenuItem
					data-ta={ids.navigationBar.avatarMenuPreferences}
					color='primary'
					onClick={() => {
						onPreferenceClick()
						closeMenu()
					}}
				>
					<ListItemIcon>
						<Icon name='Cogs' />
					</ListItemIcon>
					{t(($) => $.navigation.preferences)}
				</MenuItem>
				<MenuItem data-ta={ids.navigationBar.avatarMenuLogout} color='primary' onClick={() => logout(currentUser.id)}>
					<ListItemIcon>
						<Icon name='ArrowRightFromBracket' />
					</ListItemIcon>
					{t(($) => $.navigation.logout)}
				</MenuItem>
			</Menu>
		</>
	)
}

const AddMenu = () => {
	const { t } = useTranslation()
	const reportsRoles = useReportsRoles()
	const { data: devices } = useDevices()
	const { data: subscription } = useSubscription()
	const { openMenu, menuProps } = useMenu()
	const { windDownscalerPrototype } = useFlags()

	const canAddDevice = devices.length < subscription.maxDevices

	return (
		<>
			<Button
				data-ta={ids.addMenu.main}
				{...tourId('add-button')}
				size='large'
				startIcon={<Icon name='Plus' />}
				endIcon={<Icon name='CaretDown' />}
				onClick={openMenu}
				sx={{ boxShadow: 5 }}
			>
				{t(($) => $.navigation.add.button)}
			</Button>
			<Menu {...menuProps}>
				<Restricted feature='manage-devices' role='ADMIN'>
					<Tooltip title={canAddDevice ? '' : t(($) => $.devices.devicesLimitReached)}>
						<Box>
							<NavMenuItem data-ta={ids.addMenu.devices} color='primary' to='/devices/add' hash disabled={!canAddDevice}>
								<ListItemIcon>
									<Icon name='SignalStream' />
								</ListItemIcon>
								{t(($) => $.navigation.add.device)}
							</NavMenuItem>
						</Box>
					</Tooltip>
					<Divider />
				</Restricted>
				<Restricted feature='protections' role='ADMIN'>
					<NavMenuItem data-ta={ids.addMenu.assets} color='primary' to='/assets/add' hash>
						<ListItemIcon>
							<Icon name='MapLocationDot' />
						</ListItemIcon>
						{t(($) => $.navigation.add.asset)}
					</NavMenuItem>
				</Restricted>
				<Restricted role='ADMIN'>
					<NavMenuItem data-ta={ids.addMenu.alerts} color='primary' to='/alerts/add' hash>
						<ListItemIcon>
							<Icon name='CircleExclamation' />
						</ListItemIcon>
						{t(($) => $.navigation.add.alert)}
					</NavMenuItem>
				</Restricted>
				<Divider />
				<NavMenuItem data-ta={ids.addMenu.user} color='primary' to='/users/add'>
					<ListItemIcon>
						<Icon name='User' />
					</ListItemIcon>
					{t(($) => $.navigation.add.user)}
				</NavMenuItem>
				<NavMenuItem data-ta={ids.addMenu.userList} color='primary' to='/users/list/{-$id}'>
					<ListItemIcon>
						<Icon name='UserGroup' />
					</ListItemIcon>
					{t(($) => $.navigation.add.userList)}
				</NavMenuItem>
				<Divider />
				<Restricted feature='analyze' role={reportsRoles}>
					<NavMenuItem
						data-ta={ids.addMenu.analysisReport}
						color='primary'
						to='/reports/add'
						search={({ locationId }) => ({ locationIds: locationId ? [locationId] : undefined })}
					>
						<ListItemIcon>
							<Icon name='ChartPie' />
						</ListItemIcon>
						{t(($) => $.navigation.add.lightningAnalysisReport)}
					</NavMenuItem>
				</Restricted>
				{windDownscalerPrototype && (
					<Restricted role='ADMIN'>
						<Divider />
						<NavMenuItem color='primary' to='/wind/add' search={({ locationId }) => ({ locationId })}>
							<ListItemIcon>
								<Icon name='Wind' />
							</ListItemIcon>
							3D wind
						</NavMenuItem>
					</Restricted>
				)}
			</Menu>
		</>
	)
}

const ImpersonationBanner = () => {
	const { t } = useTranslation()
	const impersonation = useImpersonation()
	const { stopImpersonating } = useImpersonationActions()
	if (!impersonation) return <ImpersonationSelect />

	return (
		<Button
			data-ta={ids.smp.impersonate.stopBtn}
			size='large'
			startIcon={<Icon name='Xmark' />}
			onClick={stopImpersonating}
			sx={{ pointerEvents: 'initial', boxShadow: 5 }}
		>
			{t(($) => $.impersonation.stopImpersonating)}
		</Button>
	)
}
